import React, { useState, useEffect, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/react-hooks';

import { CredentialContext, credentialType } from '@providers';
import { useSnackbar } from '@src/providers/SnackbarProvider';

import Cart from './Cart';
import { Loading } from '@atoms';

// Type
import { CheckoutLineInput } from '__generated__/globalTypes';
import { CartInfoType, CartSubCartsLinesType } from '@src/utils/types';

// Query
import { CART } from '@src/pages/Cart/queries';
import { cart } from '@src/pages/Cart/__generated__/cart';

// Mutation
import {
  CART_SELECT_ALL,
  CARTLINES_UPDATE,
  CARTLINES_DELETE,
  CARTLINE_DELETE,
  CARTLINE_TO_WISHLIST,
  CHECKOUT_CREATE,
} from '@src/componentsNew/Cart/queries';
import { cartSelectAll } from '@src/componentsNew/Cart/__generated__/cartSelectAll';
import { cartLinesUpdate } from '@src/componentsNew/Cart/__generated__/cartLinesUpdate';
import { cartLinesDelete } from '@src/componentsNew/Cart/__generated__/cartLinesDelete';
import { cartLineDelete } from '@src/componentsNew/Cart/__generated__/cartLineDelete';
import { wishlistAddProduct } from '@src/componentsNew/Cart/__generated__/wishlistAddProduct';
import { checkoutCreate } from '@src/componentsNew/Cart/__generated__/checkoutCreate';

import {
  gaV4GetItemsFromCart,
  gaV4ViewCart,
  getCartBadgeFromCart,
  getGraphqlIdFromDBId,
  mergeMessagesAndErrors,
} from '@src/core/utils';
import { notEmpty } from '@src/utils/validation';
import { getSelectedProductIdsFromSubCart } from '@src/utils';

import { separateFreebieAndNotFreebieLines, extractCheckedStatesOfNotFreebies } from '@src/utils/cartUtil';
import { redirectToLoginWithCurrentPath } from '@src/utils/authCheck';
import { checkoutUrl } from '@src/app/routes';

const CartPage = () => {
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const { currentUser, currentCart, setCurrentCart } = useContext<credentialType>(CredentialContext);

  // State
  const [activeCart, setActiveCart] = useState(
    JSON.parse(localStorage.getItem('recentlyAddedProductType')) || 'maccaron',
  );
  const [loading, setLoading] = useState(false);
  const [maccaronCartErrorMessages, setMaccaronCartErrorMessages] = useState([]);
  const [mgsCartErrorMessages, setMgsCartErrorMessages] = useState([]);

  const [maccaronCart, setMaccaronCart] = useState<CartInfoType>(null);
  const [mgsCart, setMgsCart] = useState<CartInfoType>(null);
  const [freebies, setFreebies] = useState<CartSubCartsLinesType[]>([]);
  const [notFreebies, setNotFreebies] = useState<CartSubCartsLinesType[][]>([]);
  const [checkedStatesArray, setCheckedStatesArray] = useState([]);

  const [totalPrice, setTotalPrice] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [isAllChecked, setIsAllChecked] = useState(true);
  const [isSomeChecked, setIsSomeChecked] = useState(false);
  // const [isFreebieSellerDifferent, setIsFreebieSellerDifferent] = useState(false);

  const [hasSentGA, setHasSentGA] = useState(false);

  const setErrorAndCartAfterCartUpdateMutation = (activeCart: string, cart: CartInfoType, errors) => {
    if (errors?.length) {
      const errorMessage = errors.map((error) => {
        return {
          level: 'danger',
          message: error.message,
        };
      });

      if (activeCart === 'maccaron') {
        setMaccaronCartErrorMessages(errorMessage);
      } else if (activeCart === 'mgs') {
        setMgsCartErrorMessages(errorMessage);
      }
    } else {
      activeCart === 'maccaron' ? setMaccaronCart(cart) : setMgsCart(cart);
    }
  };

  // Query
  const { data: maccaronCartData, loading: maccaronCartLoading } = useQuery<cart>(CART, {
    fetchPolicy: 'network-only',
    variables: { token: currentCart?.maccaronCart?.token },
    onCompleted: (data) => {
      if (data?.cart?.messages) {
        setMaccaronCartErrorMessages(data?.cart?.messages);
      }

      setMaccaronCart(data?.cart);
    },
  });

  const { data: mgsCartData, loading: mgsCartLoading } = useQuery<cart>(CART, {
    fetchPolicy: 'network-only',
    variables: { token: currentCart?.mgsCart?.token },
    onCompleted: (data) => {
      if (data?.cart?.messages) {
        setMgsCartErrorMessages(data?.cart?.messages);
      }

      setMgsCart(data?.cart);
    },
  });

  // Mutation
  const [cartSelectAll, { loading: selectAllLoading }] = useMutation<cartSelectAll>(CART_SELECT_ALL, {
    onCompleted(data) {
      const cart = data?.cartSelectAll?.cart;
      const errors = data?.cartSelectAll?.errors;

      setErrorAndCartAfterCartUpdateMutation(activeCart, cart, errors);
    },
  });

  const [updateCartLines, { loading: updateLinesLoading }] = useMutation<cartLinesUpdate>(CARTLINES_UPDATE, {
    onCompleted: (data) => {
      const cart = data?.cartLineBulkUpdate?.cart;
      const errors = data?.cartLineBulkUpdate?.errors;

      setErrorAndCartAfterCartUpdateMutation(activeCart, cart, errors);
    },
  });

  const [deleteCartLines, { loading: deleteLinesLoading }] = useMutation<cartLinesDelete>(CARTLINES_DELETE, {
    onCompleted: (data) => {
      const cart = data?.cartLinesDelete?.cart;
      const errors = data?.cartLinesDelete?.errors;

      setErrorAndCartAfterCartUpdateMutation(activeCart, cart, errors);

      if (errors?.length === 0) {
        showSnackbar({ message: 'Selected items are removed' });
      }
    },
  });

  const [deleteCartLine, { loading: deleteLineLoading }] = useMutation<cartLineDelete>(CARTLINE_DELETE, {
    onCompleted: (data) => {
      const cart = data?.cartLineDelete?.cart;
      const errors = data?.cartLineDelete?.errors;

      setErrorAndCartAfterCartUpdateMutation(activeCart, cart, errors);
    },
  });

  const [cartLineToWishlist, { loading: cartLineToWishlistLoading }] = useMutation<wishlistAddProduct>(
    CARTLINE_TO_WISHLIST,
    {
      onCompleted: (data) => {
        const cart = data?.wishlistAddProduct?.cart;
        const errors = data?.wishlistAddProduct?.errors;

        setErrorAndCartAfterCartUpdateMutation(activeCart, cart, errors);
      },
    },
  );

  const [createCheckout, { loading: checkoutLoading }] = useMutation<checkoutCreate>(CHECKOUT_CREATE, {
    onCompleted: (data) => {
      const errorMessages = mergeMessagesAndErrors(
        data?.checkoutCreate?.checkout?.messages,
        data?.checkoutCreate?.errors,
      );

      if (data?.checkoutCreate?.errors?.length) {
        if (activeCart === 'maccaron') {
          setMaccaronCartErrorMessages(errorMessages);
        } else if (activeCart === 'mgs') {
          setMgsCartErrorMessages(errorMessages);
        }
      } else {
        if (!localStorage.getItem('recentlyAddedProductType')) {
          localStorage.setItem('recentlyAddedProductType', JSON.stringify(activeCart));
        }

        history.push(checkoutUrl);
      }
    },
  });

  useEffect(() => {
    const isLoading =
      maccaronCartLoading ||
      mgsCartLoading ||
      selectAllLoading ||
      updateLinesLoading ||
      deleteLinesLoading ||
      deleteLineLoading ||
      cartLineToWishlistLoading ||
      checkoutLoading;

    setLoading(isLoading);
  }, [
    maccaronCartLoading,
    mgsCartLoading,
    selectAllLoading,
    updateLinesLoading,
    deleteLinesLoading,
    deleteLineLoading,
    cartLineToWishlistLoading,
    checkoutLoading,
  ]);

  useEffect(() => {
    const currentActiveCart = activeCart === 'maccaron' ? maccaronCart : mgsCart;
    if (notEmpty(currentActiveCart)) {
      updateCartOnDataChange(currentActiveCart);

      if (!hasSentGA && currentActiveCart) {
        // send data to GA only once
        const cart = currentActiveCart;
        const value = cart?.totalPrice?.gross?.amount ?? 0;
        const items = gaV4GetItemsFromCart(cart);
        gaV4ViewCart(items, value);
        setHasSentGA(true);
      }
    }
  }, [activeCart, maccaronCart, mgsCart]);

  const updateCartOnDataChange = (cart: CartInfoType) => {
    const { freebies, notFreebies } = separateFreebieAndNotFreebieLines<CartSubCartsLinesType>(cart);
    const checkedStatesOfNotFreebies = extractCheckedStatesOfNotFreebies(notFreebies);

    setFreebies(freebies);
    setNotFreebies(notFreebies);
    setCheckedStatesArray(checkedStatesOfNotFreebies);

    const originalTotalPrice = cart?.originalTotal?.gross?.amount;
    const finalTotalPrice = cart?.totalPrice?.gross?.amount;

    setTotalPrice(originalTotalPrice);
    setTotalDiscount(originalTotalPrice - finalTotalPrice);

    setIsAllChecked(checkedStatesOfNotFreebies.every((row) => row.every((item) => item)));
    setIsSomeChecked(checkedStatesOfNotFreebies.some((row) => row.some((item) => item)));
    // setIsFreebieSellerDifferent(notFreebies.reduce((a, b) => a || b.length === 0, false));

    if (cart?.token) {
      setCurrentCart(getCartBadgeFromCart(cart));
    }
  };

  const handleClickSelectAllCheckbox = (e: any) => {
    const currentActiveCart = activeCart === 'maccaron' ? currentCart?.maccaronCart : currentCart?.mgsCart;

    if (currentActiveCart?.quantity > 0) {
      const cartId = getGraphqlIdFromDBId(currentActiveCart?.token, 'Cart');
      const selected = e.target.checked;

      if (notEmpty(cartId)) {
        const variables = { id: cartId, selected };

        cartSelectAll({ variables });
      }
    }
  };

  const getLinesForRemove = () => {
    let lines: string[] = [];

    notFreebies.forEach((seller, i) => {
      seller.forEach((line, j) => {
        if (checkedStatesArray[i][j]) {
          lines.push(line.id);
        }
      });
    });
    return lines;
  };

  const handleClickRemoveAllButton = (e: any) => {
    const cartId = getGraphqlIdFromDBId(
      activeCart === 'maccaron' ? currentCart?.maccaronCart?.token : currentCart?.mgsCart?.token,
      'Cart',
    );
    const lines = getLinesForRemove();

    if (lines.length === 0) {
      showSnackbar({ message: 'Select at least one item' });
    } else {
      const variables = { cartId, lines };

      deleteCartLines({ variables });
    }
  };

  const getLinesForCheckout = () => {
    let lines: CheckoutLineInput[] = [];

    freebies.forEach((freebie) => {
      lines.push({ quantity: freebie.quantity, variantId: freebie.variant.id });
    });

    notFreebies.forEach((seller, i) => {
      seller.forEach((line, j) => {
        if (checkedStatesArray[i][j]) {
          lines.push({ quantity: line.quantity, variantId: line.variant.id });
        }
      });
    });

    return lines;
  };

  // Checkout Button Click
  const handleClickCheckoutButton = (e: any) => {
    if (currentUser?.token) {
      const lines = getLinesForCheckout();

      const variables = {
        input: {
          lines,
          forMgs: activeCart === 'mgs' ? true : false,
        },
      };
      createCheckout({ variables });
    } else {
      redirectToLoginWithCurrentPath(history);
    }
  };

  const handleChangeCartType = (activeCart: 'maccaron' | 'mgs') => {
    const newCartType = activeCart === 'maccaron' ? 'mgs' : 'maccaron';

    setActiveCart(newCartType);
    localStorage.setItem('recentlyAddedProductType', JSON.stringify(newCartType));
  };

  if (maccaronCartLoading || mgsCartLoading) {
    return <Loading />;
  }

  return (
    <Cart
      activeCart={activeCart}
      loading={loading}
      checkedStatesArray={checkedStatesArray}
      isAllChecked={isAllChecked}
      activeCartLineCount={activeCart === 'maccaron' ? maccaronCart?.lineCount : mgsCart?.lineCount}
      inActiveCartLineCount={activeCart === 'maccaron' ? mgsCart?.lineCount : maccaronCart?.lineCount}
      selectedLineCount={activeCart === 'maccaron' ? maccaronCart?.selectedLineCount : mgsCart?.selectedLineCount}
      subcarts={activeCart === 'maccaron' ? maccaronCart?.subcarts : mgsCart?.subcarts}
      freebies={freebies}
      errorMessages={activeCart === 'maccaron' ? maccaronCartErrorMessages : mgsCartErrorMessages}
      totalPrice={totalPrice}
      totalDiscount={totalDiscount}
      shippingPrice={activeCart === 'maccaron' ? maccaronCart?.shippingPrice : mgsCart?.shippingPrice}
      isSomeChecked={isSomeChecked}
      totalWeightSelected={activeCart === 'maccaron' ? maccaronCart?.totalWeightSelected : mgsCart?.totalWeightSelected}
      selectedProductIDs={getSelectedProductIdsFromSubCart(
        activeCart === 'maccaron' ? maccaronCart?.subcarts : mgsCart?.subcarts,
      )}
      updateCartLine={updateCartLines}
      deleteCartLine={deleteCartLine}
      cartLineToWishlist={cartLineToWishlist}
      onClickChangeCartType={handleChangeCartType}
      onClickSelectAllCheckbox={handleClickSelectAllCheckbox}
      onClickRemoveAllButton={handleClickRemoveAllButton}
      onClickCheckoutButton={handleClickCheckoutButton}
    />
  );
};

export default CartPage;
